import React, { useState, useRef, useEffect, useContext } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import { DirectionContext } from "../App";

const DOCUMENT_TYPE = "additional-evidence";
const DOCUMENT_KEY_CANDIDATES = [
  "additionalEvidence",
  "additional-evidence",
  "additional_evidence",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const pickExistingDocument = (applicationDocuments) => {
  if (!applicationDocuments) return null;
  for (const key of DOCUMENT_KEY_CANDIDATES) {
    if (applicationDocuments[key]?.fileKey) {
      return applicationDocuments[key];
    }
  }
  return null;
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

const AdditionalEvidenceDonePage = () => {
  const { navigateTo } = useContext(DirectionContext);
  const fileInputRef = useRef(null);

  const [application, setApplication] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = Cookies.get("additionalEvidenceToken");
    if (!token) {
      navigateTo("/additionalevidence/auth");
      return;
    }

    const applicationCookie = Cookies.get("additionalEvidenceApplication");
    if (!applicationCookie) {
      navigateTo("/additionalevidence/auth");
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(applicationCookie);
    } catch (e) {
      navigateTo("/additionalevidence/auth");
      return;
    }

    setApplication(parsed);

    const load = async () => {
      try {
        if (!parsed.phoneNumber || !parsed.accessCode) {
          setIsLoading(false);
          return;
        }

        const verifyResponse = await fetch(
          `${API_ENDPOINTS.BASE_URL}/applications/verify-access`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              phoneNumber: parsed.phoneNumber,
              accessCode: parsed.accessCode,
              lawsuitId: 3,
            }),
          },
        );

        if (!verifyResponse.ok) {
          setIsLoading(false);
          return;
        }

        const latest = await verifyResponse.json();
        setApplicationData(latest);

        const existing = pickExistingDocument(latest.applicationDocuments);
        if (existing?.fileKey) {
          const targetNumber =
            latest.applicationNumber || parsed.applicationNumber;
          if (targetNumber) {
            navigateTo(`/status/additional-evidence/${targetNumber}`);
            return;
          }
        }
      } catch (err) {
        console.error("기존 증거 자료 조회 실패:", err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [navigateTo]);

  const handlePickFile = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    setErrorMessage("");
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (!selected) return;

    if (!selected.type.startsWith("image/")) {
      setErrorMessage("이미지 파일만 업로드할 수 있습니다.");
      return;
    }

    if (selected.size > MAX_FILE_SIZE) {
      setErrorMessage("이미지 용량은 10MB 이하만 가능합니다.");
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const handleClearFile = () => {
    if (isUploading) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl("");
    setErrorMessage("");
  };

  const handleUpload = async () => {
    if (!file || isUploading) return;
    setErrorMessage("");

    if (!application) {
      setErrorMessage("신청서 정보를 찾을 수 없습니다.");
      return;
    }

    const token = Cookies.get("additionalEvidenceToken");
    if (!token) {
      setErrorMessage("인증 정보가 없습니다. 다시 인증해주세요.");
      navigateTo("/additionalevidence/auth");
      return;
    }
    if (!application.id || isNaN(Number(application.id))) {
      setErrorMessage("신청서 ID를 찾을 수 없습니다.");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file, file.name);

      const uploadUrl = `${API_ENDPOINTS.BASE_URL}/uploads/lawsuit/duo/${DOCUMENT_TYPE}?applicationId=${application.id}`;
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok && response.status !== 201) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || "업로드에 실패했습니다.");
      }

      navigateTo("/additionalevidence/complete");
    } catch (error) {
      setErrorMessage(error.message || "업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  const userName =
    applicationData?.name || applicationData?.client?.name || "-";
  const applicationNumberDisplay =
    applicationData?.accessCode || application?.accessCode || "-";
  const createdAtDisplay = formatDate(applicationData?.createdAt);

  const submitLabel = isUploading ? "제출 중..." : "증거자료 제출하기";

  if (isLoading) {
    return (
      <PageBackground>
        <Container>
          <Content>
            <LoadingBox>신청서 정보를 불러오는 중...</LoadingBox>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <Container>
        <Content>
          <Header>
            <SubTitle>집단소송 신청정보</SubTitle>
            <Description>본인인증으로 확인된 신청정보입니다</Description>
          </Header>

          <SectionLabel>신청자 정보</SectionLabel>
          <UserInfoCard>
            <UserInfoRow>
              <UserInfoLabel>신청인 명</UserInfoLabel>
              <UserInfoValue>{userName}</UserInfoValue>
            </UserInfoRow>
            <UserInfoDivider />
            <UserInfoRow>
              <UserInfoLabel>신청서 번호</UserInfoLabel>
              <UserInfoValue $mono>{applicationNumberDisplay}</UserInfoValue>
            </UserInfoRow>
            <UserInfoDivider />
            <UserInfoRow>
              <UserInfoLabel>접수일자</UserInfoLabel>
              <UserInfoValue>{createdAtDisplay}</UserInfoValue>
            </UserInfoRow>
          </UserInfoCard>

          <SectionLabel>캡처화면 제출</SectionLabel>

          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />

          {!previewUrl && (
            <UploadBox
              onClick={handlePickFile}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handlePickFile();
              }}
            >
              <UploadBoxText>이미지 업로드</UploadBoxText>
            </UploadBox>
          )}

          {previewUrl && (
            <PreviewWrapper>
              <PreviewImage src={previewUrl} alt="증거 자료 미리보기" />
              <RemoveButton onClick={handleClearFile}>×</RemoveButton>
            </PreviewWrapper>
          )}

          {file && (
            <FileNameRow>
              <FileName>{file.name}</FileName>
              <FileSize>{(file.size / 1024 / 1024).toFixed(2)}MB</FileSize>
            </FileNameRow>
          )}

          {errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}

          <ButtonsContainer>
            <ActionButton
              onClick={handleUpload}
              whileHover={{ scale: file && !isUploading ? 1.02 : 1 }}
              whileTap={{ scale: file && !isUploading ? 0.98 : 1 }}
              disabled={!file || isUploading}
            >
              {submitLabel}
            </ActionButton>
          </ButtonsContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
  }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f7f7f7;

  @media (max-width: 768px) {
    padding: 0 0 80px 0;
    max-width: 100%;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px;
  }
`;

const LoadingBox = styled.div`
  width: 100%;
  padding: 80px 24px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 8px 0;
  word-break: keep-all;
  color: #111827;
`;

const Description = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
  word-break: keep-all;
  color: #6b7280;
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 10px;
`;

const UserInfoCard = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  margin-bottom: 28px;
  box-sizing: border-box;
  overflow: hidden;
`;

const UserInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  gap: 12px;
`;

const UserInfoDivider = styled.div`
  height: 1px;
  width: 100%;
  background-color: #f0f0f0;
`;

const UserInfoLabel = styled.span`
  font-size: 14px;
  color: #999;
  font-weight: 500;
  flex-shrink: 0;
`;

const UserInfoValue = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 600;
  text-align: right;
  word-break: break-all;
  font-family: ${(props) =>
    props.$mono ? "monospace, Menlo, Consolas, monospace" : "inherit"};
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadBox = styled.div`
  width: 100%;
  min-height: 260px;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #ffffff;
  margin-bottom: 10px;
  transition:
    border-color 0.2s,
    background-color 0.2s;

  &:hover {
    border-color: #c0c0c0;
    background-color: #fafafa;
  }

  &:focus {
    outline: none;
    border-color: #c0c0c0;
  }
`;

const UploadBoxText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const PreviewWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  background-color: #fafafa;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 18px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const FileNameRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 0 4px;
  margin-bottom: 16px;
`;

const FileName = styled.span`
  font-size: 13px;
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
`;

const ErrorBox = styled.div`
  width: 100%;
  padding: 12px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 16px;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  background: ${(props) => (props.disabled ? "#E0898A" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#DDC2C2" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  transition: background-color 0.2s ease;
`;

export default AdditionalEvidenceDonePage;
